import {
  createTRPCProxyClient,
  httpBatchLink,
  TRPCClientError,
} from "@trpc/client";
import { AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY, TRPC_ENDPOINT } from "../constants";

// Simple type definition for the router - will be replaced by actual types at build time
type AppRouter = any;

/**
 * Enhanced shared tRPC client
 *
 * This module provides a consistent client for shared code,
 * but delegates actual token storage to the host platform.
 * This allows web/mobile to implement storage differently.
 */

// Interface for token storage that host platforms must implement
export interface TokenStorage {
  getAuthToken: () => string | null;
  getRefreshToken: () => string | null;
  setAuthToken: (token: string, expiry?: number) => void;
  setRefreshToken: (token: string, expiry?: number) => void;
  clearTokens: () => void;
}

// Default implementation that uses localStorage
// This is primarily for bootstrapping, platforms should provide their own implementation
const defaultTokenStorage: TokenStorage = {
  getAuthToken: () => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(AUTH_TOKEN_KEY);
  },
  getRefreshToken: () => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  setAuthToken: (token: string) => {
    if (typeof window === "undefined") return;
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  },
  setRefreshToken: (token: string) => {
    if (typeof window === "undefined") return;
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },
  clearTokens: () => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

// Current token storage implementation
let currentTokenStorage: TokenStorage = defaultTokenStorage;

// Set the token storage implementation
export const setTokenStorage = (tokenStorage: TokenStorage) => {
  currentTokenStorage = tokenStorage;
};

// Create tRPC client with customizable base URL
export const createTrpcClient = (baseUrl = TRPC_ENDPOINT) => {
  return createTRPCProxyClient<AppRouter>({
    links: [
      httpBatchLink({
        url: baseUrl,
        headers: () => {
          const headers: Record<string, string> = {};

          // Get token from the current token storage
          const token = currentTokenStorage.getAuthToken();
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }

          return headers;
        },
      }),
    ],
  });
};

// Export a default client instance
export const trpcClient = createTrpcClient();

// Error handler for tRPC errors
export const handleTrpcError = (
  error: unknown,
): { message: string; status?: number } => {
  // Default error message
  let message = "An unexpected error occurred";
  let status: number | undefined = undefined;

  if (error instanceof TRPCClientError) {
    // Extract error details
    const trpcError = error as TRPCClientError<any>;
    status = trpcError.data?.httpStatus;

    if (trpcError.message) {
      message = trpcError.message;
    }

    if (trpcError.data?.message) {
      message = trpcError.data.message;
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  return { message, status };
};
